import { motion, useMotionValue, useTransform, animate, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState, useMemo } from "react";

function AnimatedNumber({ value, suffix = "", prefix = "" }: { value: number; suffix?: string; prefix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  const motionVal = useMotionValue(0);
  const rounded = useTransform(motionVal, (v) => {
    if (suffix === "%") return `${prefix}${v.toFixed(value % 1 !== 0 ? 2 : 0)}${suffix}`;
    return `${prefix}${v.toFixed(2)}${suffix}`;
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          animate(motionVal, value, { duration: 2, ease: "easeOut" });
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasAnimated, motionVal, value]);

  return (
    <span ref={ref}>
      <motion.span>{rounded}</motion.span>
    </span>
  );
}

const stats = [
  { value: 54.93, prefix: "$", suffix: "", label: "Average cost per lead for law firm clients through Meta Ads" },
  { value: 17.62, prefix: "", suffix: "%", label: "Average drop in cost per lead through ongoing campaign work" },
  { value: 120, prefix: "", suffix: "%", label: "Increase in lead conversions at an immigration firm after adding automated follow-up", source: "Source: Lawmatics / LFM Academy" },
];

const cardInitial = { opacity: 0, y: 24 };
const cardAnimate = { opacity: 1, y: 0 };

export function SoundFamiliar() {
  const reduced = useReducedMotion();
  const transitions = useMemo(
    () => stats.map((_, i) => (reduced ? {} : { delay: i * 0.15, duration: 0.5 })),
    [reduced]
  );

  return (
    <div id="results" className="h-full w-full flex items-center justify-center bg-foreground text-background px-2 py-3 sm:p-6 md:p-10 relative">
      <div className="max-w-7xl mx-auto w-full text-center relative z-10">
        <h2 className="text-lg sm:text-3xl md:text-5xl font-bold leading-tight">
          Results that speak for themselves.
        </h2>

        <div className="grid grid-cols-3 gap-1.5 sm:gap-4 mt-2 sm:mt-8">
          {stats.map((s, i) => (
            <motion.div
              key={i}
              initial={reduced ? {} : cardInitial}
              whileInView={reduced ? {} : cardAnimate}
              viewport={{ once: true, amount: 0.3 }}
              transition={transitions[i]}
              className="flex flex-col items-center border border-background/20 rounded-lg sm:rounded-2xl px-1.5 py-2 sm:p-6 bg-background/5 backdrop-blur-sm shadow-sm will-change-transform"
            >
              <p className="text-lg sm:text-4xl md:text-5xl font-bold text-primary leading-none">
                <AnimatedNumber value={s.value} prefix={s.prefix} suffix={s.suffix} />
              </p>
              <p className="mt-1 sm:mt-2 text-[10px] sm:text-sm max-w-xs opacity-80 leading-snug">{s.label}</p>
              {s.source && <p className="mt-1 text-[9px] sm:text-[10px] opacity-50 hidden sm:block">{s.source}</p>}
            </motion.div>
          ))}
        </div>

        <p className="mt-2 sm:mt-4 text-[8px] sm:text-[9px] opacity-50 max-w-2xl mx-auto leading-snug">
          <span className="hidden sm:inline">FuzionLabs campaign results — individual results will vary. | 120% figure from a third-party immigration firm case study, a separate result from FuzionLabs campaigns — results will vary.</span>
          <span className="sm:hidden">Individual results will vary.</span>
        </p>
      </div>
    </div>
  );
}
